import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import AiScheduleEditor from '../components/AiScheduleEditor';
import { ArrowLeft, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAiSchedulePage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/clients/${clientId}/ai-schedule`);
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load AI schedule', { description: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const onSave = async (enabled, schedule) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/clients/${clientId}/ai-schedule`, { enabled, schedule });
      setData((prev) => ({ ...prev, ...r.data }));
      return r.data;
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <AdminLayout><div className="p-8 text-gray-500">Loading AI schedule…</div></AdminLayout>;
  }
  if (!data) {
    return <AdminLayout><div className="p-8 text-gray-500">Schedule not available.</div></AdminLayout>;
  }

  const isAi = data.client?.client_type === 'ai';

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto pb-32" data-testid="admin-ai-schedule-page">
        <button
          onClick={() => navigate('/admin/clients')}
          className="text-gray-400 hover:text-white text-sm flex items-center gap-1.5 mb-6"
          data-testid="ai-schedule-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Venues
        </button>

        <div className="mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-[0.2em] mb-1 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#00FFFF]" /> AI Day-Plan
          </p>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-white" style={{ fontFamily: 'Outfit' }} data-testid="ai-schedule-client-name">
            {data.client?.name}
          </h1>
          <p className="text-gray-400 text-sm mt-1">{data.client?.email}</p>
        </div>

        {!isAi && (
          <div className="mb-6 flex items-start gap-3 p-4 rounded-2xl bg-[#FFB300]/10 border border-[#FFB300]/30" data-testid="ai-schedule-not-ai-warning">
            <AlertTriangle className="w-5 h-5 text-[#FFB300] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-[#FFB300]">
              This venue is a <span className="font-bold">commercial</span> venue — the AI day-plan only drives
              in-house AI-library venues. Switch its type to AI first for this schedule to take effect.
            </p>
          </div>
        )}

        <AiScheduleEditor
          enabled={data.enabled}
          schedule={data.schedule}
          genres={data.genres || []}
          energies={data.energies || []}
          onSave={onSave}
          saving={saving}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminAiSchedulePage;
